export type DateDisplayFormat = 'indian' | 'american';

export const DEFAULT_DATE_DISPLAY_FORMAT: DateDisplayFormat = 'indian';

let activeDateDisplayFormat: DateDisplayFormat = DEFAULT_DATE_DISPLAY_FORMAT;

export const isDateDisplayFormat = (value: unknown): value is DateDisplayFormat => (
  value === 'indian' || value === 'american'
);

export const normalizeDateDisplayFormat = (value: unknown): DateDisplayFormat => (
  isDateDisplayFormat(value) ? value : DEFAULT_DATE_DISPLAY_FORMAT
);

export const setActiveDateDisplayFormat = (format: unknown) => {
  activeDateDisplayFormat = normalizeDateDisplayFormat(format);
};

const parseDateOnly = (value: string): Date | null => {
  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return new Date(Date.UTC(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]), 12));
  }
  const indianMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (indianMatch) {
    return new Date(Date.UTC(Number(indianMatch[3]), Number(indianMatch[2]) - 1, Number(indianMatch[1]), 12));
  }
  return null;
};

export const toDisplayDate = (value: Date | string | number): Date | null => {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const dateOnly = typeof value === 'string' ? parseDateOnly(value.trim()) : null;
  if (dateOnly) return dateOnly;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export type DisplayDateOptions = {
  format?: DateDisplayFormat;
  timeZone?: string;
  fallback?: string;
};

export const formatDisplayDate = (
  value: Date | string | number | null | undefined,
  options: DisplayDateOptions = {},
) => {
  if (value === null || value === undefined || value === '') return options.fallback ?? '';
  const date = toDisplayDate(value);
  if (!date) return options.fallback ?? String(value);
  const format = normalizeDateDisplayFormat(options.format ?? activeDateDisplayFormat);
  return new Intl.DateTimeFormat(format === 'american' ? 'en-US' : 'en-GB', {
    day: '2-digit',
    month: format === 'american' ? 'short' : '2-digit',
    year: 'numeric',
    ...(options.timeZone ? { timeZone: options.timeZone } : {}),
  }).format(date);
};

export const formatDisplayDateTime = (
  value: Date | string | number | null | undefined,
  options: DisplayDateOptions & Intl.DateTimeFormatOptions = {},
) => {
  const dateText = formatDisplayDate(value, options);
  const date = value === null || value === undefined || value === '' ? null : toDisplayDate(value);
  if (!date || !dateText) return dateText;
  const {
    format: _format,
    fallback: _fallback,
    day: _day,
    month: _month,
    year: _year,
    ...timeOptions
  } = options;
  const timeText = new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    ...timeOptions,
  }).format(date);
  return `${dateText}, ${timeText}`;
};
